/**
 * First-touch attribution helpers.
 *
 * Lifecycle:
 *   1. <AttributionCapture/> mounts in the client root layout. On mount it
 *      inspects URL params + document.referrer.
 *   2. If new attribution params are present (utm_*, gclid, fbclid), it
 *      writes them to localStorage. Existing attribution is preserved when
 *      no new params are seen — that's the "first-touch" guarantee.
 *   3. <BrochureForm/> reads attribution via `getAttribution()` and includes
 *      it in the POST/PATCH payload. The server (lead-api.ts) writes it to
 *      the brochure_leads row.
 *
 * 30-day TTL matches Google Ads default attribution window.
 */

export const ATTRIBUTION_KEY = "bf_attribution";
export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  /** Privacy-safe Google Ads click-id voor iOS Safari + iOS apps. Komt
   *  ipv gclid zodra Apple's ITP de gclid blokkeert. Sinds Mar-2022. */
  gbraid?: string;
  /** Privacy-safe Google Ads click-id voor web bij iOS (variant van gbraid). */
  wbraid?: string;
  fbclid?: string;
  referrer?: string;
  landing_path?: string;
  /** ms since epoch — used to enforce the 30-day TTL */
  captured_at: number;
}

interface StoredAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  referrer?: string;
  landing_path?: string;
  captured_at?: unknown;
}

function readStored(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttribution;
    const captured = typeof parsed.captured_at === "number" ? parsed.captured_at : 0;
    if (!captured || Date.now() - captured > ATTRIBUTION_TTL_MS) return null;
    return { ...parsed, captured_at: captured };
  } catch {
    return null;
  }
}

function writeStored(a: Attribution): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(a));
  } catch {
    // localStorage full or blocked — silently drop.
  }
}

const ATTR_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
] as const;

/**
 * Inspects current URL + document.referrer. If new attribution params are
 * present (or no attribution exists yet), writes them to localStorage.
 * First-touch: an existing entry is NOT overwritten unless new utm_* / click
 * ids are seen.
 *
 * Safe to call on every route — it's idempotent within an attribution
 * window.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  // 100-char cap voor utm_*-velden (DB heeft varchar(200) maar lange utm's
  // zijn vrijwel altijd noise). NIET voor click-IDs — Meta klaagt anders
  // 'Server sending modified fbclid value in fbc parameter' (3% Lead events
  // affected) omdat we fbclid truncateerden op 100 chars. Click-IDs moeten
  // EXACT zijn zoals het netwerk ze in de URL plaatste — Meta/Google
  // valideren ze server-side bij attribution-matching.
  const trimUtm = (s: string | null) => (s ? s.slice(0, 100) : undefined);
  const trimClickId = (s: string | null) => (s ? s.slice(0, 500) : undefined);

  const incoming: Record<(typeof ATTR_PARAMS)[number], string | undefined> = {
    utm_source: trimUtm(params.get("utm_source")),
    utm_medium: trimUtm(params.get("utm_medium")),
    utm_campaign: trimUtm(params.get("utm_campaign")),
    utm_term: trimUtm(params.get("utm_term")),
    utm_content: trimUtm(params.get("utm_content")),
    gclid: trimClickId(params.get("gclid")),
    gbraid: trimClickId(params.get("gbraid")),
    wbraid: trimClickId(params.get("wbraid")),
    fbclid: trimClickId(params.get("fbclid")),
  };
  const hasIncoming = ATTR_PARAMS.some((k) => Boolean(incoming[k]));

  const existing = readStored();
  if (existing && !hasIncoming) {
    // Preserve first-touch — no fresh signals to record.
    return;
  }

  // External referrer only (own-domain navigations don't count as a source).
  let referrer: string | undefined;
  if (typeof document !== "undefined" && document.referrer) {
    try {
      const u = new URL(document.referrer);
      if (u.hostname && u.hostname !== window.location.hostname) {
        referrer = document.referrer.slice(0, 500);
      }
    } catch {
      // malformed referrer
    }
  }

  // Sticky-merge: new utm_* fields overwrite, missing ones keep existing.
  // exactOptionalPropertyTypes forbids `undefined` literal as a value, so we
  // build the object via a writable Record and only assign defined values.
  const next: Record<string, string | number> = {
    captured_at: existing?.captured_at ?? Date.now(),
    landing_path:
      existing?.landing_path ?? window.location.pathname.slice(0, 200),
  };
  const setIfDefined = (k: string, v: string | undefined) => {
    if (typeof v === "string" && v.length > 0) next[k] = v;
  };
  setIfDefined("utm_source", incoming.utm_source ?? existing?.utm_source);
  setIfDefined("utm_medium", incoming.utm_medium ?? existing?.utm_medium);
  setIfDefined("utm_campaign", incoming.utm_campaign ?? existing?.utm_campaign);
  setIfDefined("utm_term", incoming.utm_term ?? existing?.utm_term);
  setIfDefined("utm_content", incoming.utm_content ?? existing?.utm_content);
  setIfDefined("gclid", incoming.gclid ?? existing?.gclid);
  setIfDefined("gbraid", incoming.gbraid ?? existing?.gbraid);
  setIfDefined("wbraid", incoming.wbraid ?? existing?.wbraid);
  setIfDefined("fbclid", incoming.fbclid ?? existing?.fbclid);
  setIfDefined("referrer", existing?.referrer ?? referrer);

  // If this is the first time we see utm_*/click-id but no existing entry,
  // landing_path = current path (which is correct — that IS the landing).
  if (!existing && hasIncoming) {
    next["landing_path"] = window.location.pathname.slice(0, 200);
    next["captured_at"] = Date.now();
  }

  writeStored(next as unknown as Attribution);

  // Wanneer een verse fbclid binnenkomt: schrijf óók een _fbc cookie zodat
  // (a) de browser-pixel hem doorgeeft op subsequent events, (b) wij hem
  // kunnen lezen voor CAPI dispatch via document.cookie. Meta verwacht
  // formaat `fb.<subdomainIndex>.<clickTimestamp>.<fbclid>`.
  if (incoming.fbclid) {
    ensureFbcCookie(incoming.fbclid);
  }
}

/**
 * Schrijft een `_fbc` cookie in Meta's voorgeschreven formaat. Doet niets
 * als er al een _fbc cookie staat (browser-pixel zet 'm met betere
 * subdomain-accuracy).
 *
 * Meta docs: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 */
export function ensureFbcCookie(fbclid: string): void {
  if (typeof document === "undefined") return;
  if (!fbclid) return;
  if (document.cookie.split("; ").some((c) => c.startsWith("_fbc="))) return;
  const value = `fb.1.${Date.now()}.${fbclid}`;
  // 90 dagen — matched Meta's default attribution window voor click events.
  const maxAge = 90 * 24 * 60 * 60;
  // Geen Domain= zodat de cookie host-only blijft (current host). Voor
  // subdomain-sharing zou je `; domain=.badkamer-wijzer.nl` kunnen toevoegen,
  // maar dat is fragiel bij multi-domain whitelabel — pixel doet eigen logic.
  document.cookie = `_fbc=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/**
 * Returns the current attribution from localStorage, or null. Strips the
 * `captured_at` timestamp so the result is ready to spread into a fetch
 * body.
 */
export function getAttribution(): Omit<Attribution, "captured_at"> | null {
  const stored = readStored();
  if (!stored) return null;
  const { captured_at: _captured, ...rest } = stored;
  // Drop empty fields so we don't write empty strings to the DB.
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (typeof v === "string" && v.length > 0) out[k] = v;
  }
  return Object.keys(out).length > 0 ? (out as Omit<Attribution, "captured_at">) : null;
}
